import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import type { TrajectoryState } from "@/lib/behaviorRuntime";
import type { RhythmResult } from "@/lib/rhythmEngine";
import { cn } from "@/lib/utils";

interface Props {
  state: TrajectoryState | null;
  rhythm?: RhythmResult | null;
}

type Severity = "ok" | "warn" | "alert";

const sev = (v: number, inverted = false): Severity => {
  const x = inverted ? 100 - v : v;
  if (x >= 65) return "alert";
  if (x >= 40) return "warn";
  return "ok";
};

const sevColor: Record<Severity, string> = {
  ok: "text-cyan/85",
  warn: "text-amber-300/90",
  alert: "text-rose-400/95",
};

const sevDot: Record<Severity, string> = {
  ok: "bg-cyan/80",
  warn: "bg-amber-400/90",
  alert: "bg-rose-400/95",
};

const trendLabel = (t: TrajectoryState["pressureTrend"]) =>
  t === "rising" ? "Rising" : t === "falling" ? "Easing" : "Stable";

const numLabel = (v: number) => (v >= 65 ? "High" : v >= 40 ? "Moderate" : "Low");

const Row = ({
  label,
  value,
  display,
  inverted = false,
}: {
  label: string;
  value: number;
  display?: string;
  inverted?: boolean;
}) => {
  const s = sev(value, inverted);
  return (
    <div className="flex items-center justify-between gap-3 py-[3px]">
      <span className="text-[9.5px] font-mono tracking-[0.22em] uppercase text-muted-foreground/55">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span className={cn("w-1 h-1 rounded-full", sevDot[s])} />
        <span className={cn("text-[10px] font-mono uppercase tracking-[0.18em]", sevColor[s])}>
          {display ?? numLabel(value)}
        </span>
      </div>
    </div>
  );
};

const LiveTrajectoryPanel = ({ state, rhythm }: Props) => {
  // Rolling micro-event ticker (fade in/out)
  const [eventLog, setEventLog] = useState<{ id: number; text: string }[]>([]);
  useEffect(() => {
    if (!state || state.microEvents.length === 0) return;
    const now = Date.now();
    const additions = state.microEvents.map((text, i) => ({ id: now + i, text }));
    setEventLog((prev) => [...additions, ...prev].slice(0, 4));
    const t = window.setTimeout(() => {
      setEventLog((prev) => prev.filter((e) => Date.now() - e.id < 5200));
    }, 5400);
    return () => window.clearTimeout(t);
  }, [state?.microEvents]);

  if (!state) {
    return (
      <div className="rounded-xl border border-border/40 bg-background/40 backdrop-blur-sm px-3.5 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-3 h-3 text-muted-foreground/50" />
          <span className="text-[9.5px] font-mono tracking-[0.3em] uppercase text-muted-foreground/55">
            Live Trajectory
          </span>
        </div>
        <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-muted-foreground/35">
          Awaiting first response…
        </p>
      </div>
    );
  }

  const dirSeverity: Severity =
    state.trajectoryDirection === "Conversational collapse risk" ||
    state.trajectoryDirection === "Escalation likely"
      ? "alert"
      : state.trajectoryDirection === "Emotional reversal emerging" ||
        state.trajectoryDirection === "Passive resistance building"
      ? "warn"
      : "ok";

  return (
    <div
      className={cn(
        "relative rounded-xl border bg-background/45 backdrop-blur-sm px-3.5 py-3 transition-colors duration-700",
        state.atmosphere === "volatile"
          ? "border-rose-500/25 shadow-[0_0_24px_-12px_hsl(350_85%_55%_/_0.55)]"
          : state.atmosphere === "tense"
          ? "border-amber-500/20 shadow-[0_0_22px_-14px_hsl(38_95%_55%_/_0.45)]"
          : state.atmosphere === "withdrawn"
          ? "border-border/30 opacity-90"
          : "border-cyan/20 shadow-[0_0_22px_-14px_hsl(184_100%_50%_/_0.4)]",
      )}
    >
      {/* HEADER */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Activity
            className={cn(
              "w-3 h-3",
              state.atmosphere === "volatile"
                ? "text-rose-400/85 animate-pulse"
                : state.atmosphere === "tense"
                ? "text-amber-300/85"
                : "text-cyan/80",
            )}
          />
          <span className="text-[9.5px] font-mono tracking-[0.3em] uppercase text-muted-foreground/65">
            Live Trajectory
          </span>
        </div>
        <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-muted-foreground/40">
          T{Math.max(1, state.nextPressureLevel)}
        </span>
      </div>

      {/* SIGNALS */}
      <div className="space-y-px">
        <Row
          label="Pressure"
          value={state.nextPressureLevel}
          display={trendLabel(state.pressureTrend)}
        />
        <Row label="Withdrawal" value={state.withdrawalWeight} />
        <Row label="Deflection Risk" value={state.deflectionRisk} />
        <Row label="Emotional Volatility" value={state.emotionalVolatility} />
        <Row
          label="Conversation Stability"
          value={state.conversationStability}
          inverted
        />
      </div>

      {/* TRAJECTORY DIRECTION */}
      <div className="mt-2.5 pt-2 border-t border-border/30 flex items-center justify-between gap-2">
        <span className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/45">
          Trajectory
        </span>
        <span className={cn("text-[10px] font-mono uppercase tracking-[0.18em]", sevColor[dirSeverity])}>
          {state.trajectoryDirection}
        </span>
      </div>

      {/* RHYTHM STATE — conversational cadence intelligence */}
      {rhythm && (
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/45">
            Rhythm
          </span>
          <span
            className={cn(
              "text-[10px] font-mono uppercase tracking-[0.18em]",
              rhythm.rhythmType === "escalating_pressure" ||
                rhythm.rhythmType === "compressed_conflict" ||
                rhythm.rhythmType === "emotional_flooding"
                ? "text-rose-300/90"
                : rhythm.rhythmType === "fragmented_escalation" ||
                  rhythm.rhythmType === "passive_resistance"
                ? "text-amber-300/90"
                : rhythm.rhythmType === "silence_leverage" ||
                  rhythm.rhythmType === "unstable_withdrawal" ||
                  rhythm.rhythmType === "detached_control"
                ? "text-muted-foreground/70"
                : "text-cyan/85",
            )}
          >
            {rhythm.rhythmLabel}
          </span>
        </div>
      )}

      {/* IMPACT DELTAS — last response effect */}
      {state.impactDeltas.length > 0 && (
        <div
          key={`impact-${state.nextPressureLevel}-${state.pressureDelta}`}
          className="mt-2 flex flex-wrap gap-1.5"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          {state.impactDeltas.map((d, i) => (
            <span
              key={i}
              className={cn(
                "inline-flex items-center gap-1 px-1.5 py-[2px] rounded-md border text-[9px] font-mono uppercase tracking-[0.16em]",
                d.sign === "+"
                  ? "border-rose-400/25 text-rose-300/90 bg-rose-500/[0.04]"
                  : "border-cyan/25 text-cyan/85 bg-cyan/[0.04]",
              )}
            >
              <span className="opacity-70">{d.sign}</span>
              {d.label}
            </span>
          ))}
        </div>
      )}

      {/* MICRO EVENT TICKER */}
      {eventLog.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/20 space-y-0.5">
          {eventLog.slice(0, 2).map((e, i) => (
            <p
              key={e.id}
              className={cn(
                "text-[9.5px] font-mono tracking-[0.2em] uppercase transition-opacity",
                i === 0 ? "text-amber-300/80" : "text-muted-foreground/40",
              )}
              style={{ animation: "fadeIn 0.6s ease-out" }}
            >
              › {e.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveTrajectoryPanel;
